import { useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useResetPrcVariantSectionMutation,
  type PrcVersionPayload,
  type PrcVersionSummary,
} from '@shared/api';
import { fleetLeading, fleetType, formatCount, typo } from '@entities/fleet';
import {
  isEditableStatus,
  PRC_MAIN_LABEL,
  prcObjectsLabel,
  prcObjectsPhrase,
  variantOwnSections,
  type VariantSection,
} from '@entities/prc';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import type { PrcAccess } from '../access';
import { MODELS_GEN, count } from '../format';
import { SegmentButton, Segmented } from '../ui';
import { VariantDrawer } from './VariantDrawer';

const Bar = styled.section`
  margin-top: ${({ theme }) => theme.spacing(5)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  & > h3 {
    margin: 0;
    font-size: ${fleetType.body};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
  }

  & > .spacer {
    flex: 1 1 auto;
  }
`;

const Text = styled.p`
  margin: ${({ theme }) => `${theme.spacing(3)} 0 0`};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
  overflow-wrap: anywhere;

  & > strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }
`;

const Own = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: ${({ theme }) => `${theme.spacing(2)} 0 0`};
  padding: 0;
  list-style: none;

  & > li {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1.5)};
    padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2.5)}`};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.radius.pill};
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.normal};
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const NoticeBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

/**
 * Условия расчёта версии: основной расчёт и варианты условий по районам
 * (одна калькуляция ЭММ — свои цены топлива, ФОТ, общецеховые). Выбранные
 * условия — в адресе: вкладки показывают и правят их.
 */
export function VariantBar({
  payload,
  versions,
  access,
  variantId,
  onSelect,
}: {
  payload: PrcVersionPayload;
  versions: readonly PrcVersionSummary[];
  access: PrcAccess;
  /** Выбранный вариант; `null` — основной расчёт. */
  variantId: string | null;
  onSelect: (variantId: string | null) => void;
}) {
  const v = payload.version;
  const variants = payload.variants ?? [];
  const current = variants.find((x) => x.id === variantId) ?? null;
  const editable = access.canWrite && isEditableStatus(v.status);
  const [drawer, setDrawer] = useState<'create' | 'edit' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reset, { isLoading: resetting }] = useResetPrcVariantSectionMutation();

  if (variants.length === 0 && !editable) return null;

  async function resetSection(s: VariantSection) {
    if (!current) return;
    setError(null);
    try {
      await reset({
        id: v.id,
        variantId: current.id,
        block: s.block,
        section: s.section,
        expected_updated_at: v.updated_at,
      }).unwrap();
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось вернуть раздел как в основном расчёте.'));
    }
  }

  const own = variantOwnSections(current?.own);

  return (
    <Bar aria-labelledby="prc-conditions-h">
      <Head>
        <h3 id="prc-conditions-h">Условия расчёта</h3>
        <span className="spacer" />
        {editable && (
          <TextButton type="button" onClick={() => setDrawer('create')}>
            + Вариант условий
          </TextButton>
        )}
      </Head>

      {variants.length > 0 && (
        <Segmented role="group" aria-label="Чьи условия показать">
          <SegmentButton
            type="button"
            aria-pressed={current === null}
            onClick={() => onSelect(null)}
          >
            {PRC_MAIN_LABEL}
          </SegmentButton>
          {variants.map((x) => (
            <SegmentButton
              key={x.id}
              type="button"
              aria-pressed={x.id === current?.id}
              title={x.objects.length > 0 ? prcObjectsLabel(x.objects) : 'Объекты не выбраны'}
              onClick={() => onSelect(x.id)}
            >
              {x.name}
            </SegmentButton>
          ))}
        </Segmented>
      )}

      {current === null ? (
        <Text>
          {variants.length === 0
            ? typo(
                'Одна калькуляция для нескольких районов: добавьте вариант условий. У варианта свои цены топлива, ФОТ механизаторов, общецеховые — всё, что поменяете во вкладках; модели, паспорта и лимиты общие. Цены варианта утверждаются вместе с версией на его объекты.',
              )
            : typo(
                `${PRC_MAIN_LABEL}: ${prcObjectsPhrase(v.objects)}.${editable ? ' Его правка пересчитывает и варианты — в том, чего вариант не менял.' : ''}`,
              )}
        </Text>
      ) : (
        <>
          <Text>
            <strong>{typo(`Вариант «${current.name}»`)}</strong>
            {' — '}
            {current.objects.length > 0
              ? typo(prcObjectsPhrase(current.objects))
              : typo('объекты не выбраны: без них вариант не отправить на утверждение')}
            .{' '}
            {own.length > 0
              ? typo(
                  editable
                    ? 'Свои условия варианта — ниже; остальное берётся из основного расчёта и меняется вместе с ним.'
                    : 'Свои условия варианта — ниже; остальное — как в основном расчёте.',
                )
              : typo(
                  editable
                    ? 'Пока всё как в основном расчёте. Поменяйте цены, ФОТ или параметры во вкладках — изменённый раздел станет своим для варианта.'
                    : 'Условия — как в основном расчёте.',
                )}
            {editable && (
              <>
                {' '}
                <TextButton type="button" onClick={() => setDrawer('edit')}>
                  Название, объекты, удаление
                </TextButton>
              </>
            )}
          </Text>
          {own.length > 0 && (
            <Own aria-label="Свои условия варианта">
              {own.map((s) => (
                <li key={`${s.block}:${s.section}`}>
                  {s.title}
                  {editable && (
                    <TextButton
                      type="button"
                      disabled={resetting}
                      aria-label={`Вернуть «${s.title}» как в основном расчёте`}
                      onClick={() => void resetSection(s)}
                    >
                      вернуть как в основном
                    </TextButton>
                  )}
                </li>
              ))}
            </Own>
          )}
          {current.errors > 0 && (
            <NoticeBox>
              <Notice $tone="warning" role="note">
                <span className="text">
                  {typo(
                    `В условиях варианта не посчитано ${count(current.errors, MODELS_GEN)} — их цены не утвердятся.`,
                  )}
                </span>
              </Notice>
            </NoticeBox>
          )}
        </>
      )}

      {error && (
        <NoticeBox>
          <Notice $tone="danger" role="alert">
            <span className="text">{error}</span>
            <TextButton type="button" onClick={() => setError(null)}>
              Скрыть
            </TextButton>
          </Notice>
        </NoticeBox>
      )}

      {variants.length > 0 && current === null && editable && (
        <Text>
          {typo(
            `Вариантов: ${formatCount(variants.length)}. Выберите вариант выше — вкладки покажут его цены и условия.`,
          )}
        </Text>
      )}

      {drawer && (
        <VariantDrawer
          payload={payload}
          versions={versions}
          variant={drawer === 'edit' ? current : null}
          onClose={() => setDrawer(null)}
          onSaved={(next) => {
            setDrawer(null);
            onSelect(next);
          }}
        />
      )}
    </Bar>
  );
}
